// src/components/CustomerFlow/Steps/PhoneValidationStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import Swal from 'sweetalert2';
import { PhoneField } from '../../ui';

const PhoneValidationStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  // Estados existentes
  const [isLoading, setIsLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showEditPhone, setShowEditPhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  
  // Nuevos estados para el flujo de verificación
  const [verificationStatus, setVerificationStatus] = useState(null); // 'verified' | 'pending_code' | 'should_send' | 'loading'
  const [phoneId, setPhoneId] = useState(null);
  const [requestUid, setRequestUid] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [sendAttempts, setSendAttempts] = useState(0); // Intentos de envío (máx 3)
  const [verifyAttempts, setVerifyAttempts] = useState(0); // Intentos de verificación (máx 3 por envío)
  const [isSending, setIsSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['PhoneValidationStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'PhoneValidationStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Verificar estado del teléfono al montar el componente
  useEffect(() => {
    // Verificar que flowData existe y tiene las propiedades necesarias
    if (flowData?.tokenCustomerId && flowData?.tokenRequestId) {
      checkPhoneStatus();
    } else {
      // Si no hay datos, deshabilitar loading y mostrar error
      console.warn('PhoneValidationStep: flowData incompleto:', flowData);
      setIsLoading(false);
      setValidationError('No se pudo obtener la información de la solicitud. Por favor, recarga la página.');
      // Establecer un estado por defecto para mostrar algo
      setVerificationStatus('should_send');
    }
  }, [flowData]);

  const checkPhoneStatus = async () => {
    try {
      setIsLoading(true);
      setValidationError(null);
      
      // Primero obtener el UID de la request request usando el ID
      // Necesitamos obtener el request request para tener el UID
      let resolvedRequestUid = null;
      
      try {
        // Intentar obtener el request request para obtener el UID
        const requestResponse = await api.get(
          `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}`
        );
        
        if (requestResponse.data?.uid) {
          resolvedRequestUid = requestResponse.data.uid;
        } else if (requestResponse.data?.data?.uid) {
          resolvedRequestUid = requestResponse.data.data.uid;
        }
      } catch (err) {
        console.warn('PhoneValidationStep: No se pudo obtener la request, intentando con el id directamente');
        // Si falla, intentar usar el ID como UID (puede que sea el mismo)
        resolvedRequestUid = flowData.tokenRequestId;
      }
      
      if (!resolvedRequestUid) {
        console.error('PhoneValidationStep: No se encontró requestUid');
        setValidationError('No se pudo obtener la información de la solicitud.');
        setVerificationStatus('should_send');
        setIsLoading(false);
        return;
      }
      
      console.log('PhoneValidationStep: Llamando check-status con request_uid:', resolvedRequestUid);
      
      const response = await api.get('/phone-verification/check-status', {
        params: { request_uid: resolvedRequestUid }
      });
      
      console.log('PhoneValidationStep: Respuesta check-status:', response.data);
      
      const data = response.data;
      setPhoneId(data.phone_id);
      setPhoneNumber(data.phone || '');
      setRequestUid(resolvedRequestUid);
      setCodeExpiresAt(data.expires_at);
      
      // Determinar estado
      // PRIORIDAD 1: Si el teléfono ya está verificado, completar el paso inmediatamente
      // No importa si hay un código pendiente (has_pending_code), si ya está verificado, no se necesita nada más
      if (data.is_verified) {
        // Teléfono ya verificado - avanzar automáticamente sin enviar SMS
        onNext({ 
          step_name: 'PhoneValidationStep',
          step_status: 'completed',
          phone_validated: true,
          phone_id: data.phone_id,
          verification_timestamp: new Date().toISOString()
        }, 'completed');
        return;
      }
      
      // PRIORIDAD 2: Solo si NO está verificado, verificar si requiere entrada de código
      // requires_code_entry tiene prioridad sobre has_pending_code porque es más específico
      if (data.requires_code_entry) {
        // Mostrar campo de código - el usuario debe ingresar el código
        setVerificationStatus('pending_code');
        setCodeSent(true);
      } else if (data.has_pending_code && !data.is_verified) {
        // Hay un código pendiente pero el teléfono no está verificado aún
        // Mostrar campo de código para que el usuario lo ingrese
        setVerificationStatus('pending_code');
        setCodeSent(true);
      } else if (data.should_request_code) {
        // Mostrar botón "Enviar código" - el usuario debe solicitar un nuevo código
        setVerificationStatus('should_send');
      } else {
        // Estado por defecto si no hay ninguna condición específica
        setVerificationStatus('should_send');
      }
      
    } catch (error) {
      console.error('Error checking phone status:', error);
      
      // Manejar específicamente el caso 404 - teléfono no encontrado
      if (error.response?.status === 404) {
        // Mostrar mensaje del servidor (ui_message o message)
        const errorMsg = error.response?.data?.ui_message || error.response?.data?.message || 'No hay teléfono registrado';
        setValidationError(errorMsg);
        
        // Activar formulario de ingreso de teléfono
        setShowEditPhone(true);
        
        // NO establecer verificationStatus para que no se muestre el botón de enviar
        setVerificationStatus(null);
      } else {
        // Para otros errores, mostrar mensaje y solo permitir envío si hay un número
        setValidationError(error.response?.data?.message || 'Error al verificar el estado del teléfono. Por favor, intenta nuevamente.');
        
        // Solo habilitar botón de envío si hay un número de teléfono válido
        if (phoneNumber && phoneNumber.trim() !== '') {
          setVerificationStatus('should_send');
        } else {
          setVerificationStatus(null);
          setShowEditPhone(true);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (sendAttempts >= 3) {
      setValidationError('Has alcanzado el máximo de intentos de envío. Por favor contacta al soporte.');
      return;
    }
    
    try {
      setIsSending(true);
      setSendError(null);
      setValidationError(null);
      
      const response = await api.post('/phone-verification/send', {
        phone_id: phoneId,
        request_uid: requestUid
      });
      
      if (response.status === 200) {
        setCodeSent(true);
        setVerificationStatus('pending_code');
        setSendAttempts(prev => prev + 1);
        setVerifyAttempts(0); // Reset verification attempts
      }
      
    } catch (error) {
      console.error('Error sending code:', error);
      const newSendAttempts = sendAttempts + 1;
      setSendAttempts(newSendAttempts);
      
      const errorMsg = error.response?.data?.message || 'Error al enviar el código. Intenta nuevamente.';
      
      if (newSendAttempts >= 3) {
        setSendError('Has alcanzado el máximo de intentos de envío.');
        setShowEditPhone(true); // Permitir cambiar número
      } else {
        setSendError(errorMsg);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Solo números
    if (value.length <= 6) {
      setOtpCode(value);
      setValidationError(null);
    }
  };

  const handleValidateOtp = async () => {
    if (otpCode.length !== 6) {
      setValidationError('Por favor, ingresa un código de 6 dígitos');
      return;
    }
    
    // Verificar intentos de verificación (máx 3 por envío)
    if (verifyAttempts >= 3) {
      setValidationError('Has alcanzado el máximo de intentos. Puedes reenviar el código.');
      setShowEditPhone(true);
      return;
    }
    
    try {
      setIsValidating(true);
      setValidationError(null);
      
      const response = await api.post('/phone-verification/verify', {
        phone_id: phoneId,
        code: otpCode.replace(/\s/g, ''), // Remover espacios
        request_uid: requestUid
      });
      
      if (response.status === 200) {
        // Verificación exitosa
        onNext({ 
          step_name: 'PhoneValidationStep',
          step_status: 'completed',
          phone_validated: true,
          phone_id: phoneId,
          verification_timestamp: new Date().toISOString()
        }, 'completed');
      }
      
    } catch (error) {
      console.error('Error validating OTP:', error);
      
      const newVerifyAttempts = verifyAttempts + 1;
      setVerifyAttempts(newVerifyAttempts);
      
      const errorMessage = error.response?.data?.ui_message 
        || error.response?.data?.message 
        || 'Código inválido. Por favor, intenta nuevamente.';
      
      if (newVerifyAttempts >= 3) {
        setValidationError('Has alcanzado el máximo de intentos de verificación. Puedes reenviar el código.');
        setShowEditPhone(true);
      } else {
        setValidationError(errorMessage);
      }
      
      setOtpCode(''); // Limpiar campo
      
    } finally {
      setIsValidating(false);
    }
  };

  const handlePhoneNumberChange = (payload) => {
    setNewPhoneNumber(payload?.e164 || '');
  };

  const handleUpdatePhone = async () => {
    if (!newPhoneNumber) {
      setValidationError('Por favor, ingresa un número de teléfono válido');
      return;
    }

    const isE164 = newPhoneNumber.startsWith('+');
    if ((isE164 && newPhoneNumber.length < 11) || (!isE164 && newPhoneNumber.length < 10)) {
      setValidationError('Por favor, ingresa un número de teléfono válido');
      return;
    }
    
    // Verificar si ya se usaron los 3 envíos
    if (sendAttempts >= 3) {
      setValidationError('Has alcanzado el máximo de intentos. Por favor contacta al soporte.');
      return;
    }
    
    try {
      setIsUpdatingPhone(true);
      setValidationError(null);
      
      // Usar el endpoint de retry existente
      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}/self-service/phone-validation/retry`,
        {
          new_phone_number: newPhoneNumber
        }
      );
      
      if (response.data.success) {
        // Resetear estados y volver a check status
        setShowEditPhone(false);
        setNewPhoneNumber('');
        setOtpCode('');
        setVerifyAttempts(0);
        setSendAttempts(prev => prev + 1); // Incrementar contador de envíos
        
        // Volver a verificar estado
        await checkPhoneStatus();
        
        Swal.fire({
          icon: 'success',
          title: 'Número actualizado',
          text: 'Se ha enviado un nuevo código a tu nuevo número.',
          confirmButtonText: 'Entendido'
        });
      } else {
        throw new Error(response.data.message || 'No se pudo actualizar el número de teléfono');
      }
      
    } catch (error) {
      console.error('Error updating phone number:', error);
      setValidationError(error.response?.data?.message || 'No se pudo actualizar el número de teléfono. Por favor, intenta nuevamente.');
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showEditPhone) {
        handleUpdatePhone();
      } else {
        handleValidateOtp();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="phone-validation-step bg-white p-4 rounded-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Cargando información de validación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-validation-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3 className="phone-validation-step__title">Verifica tu teléfono</h3>
      </div>

      <div className="step-body">
        {!showEditPhone ? (
          <>
            {/* Mensaje de error si no hay verificationStatus */}
            {!verificationStatus && (
              <div className="alert alert-light-warning" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <p className="mb-0">
                  Cargando información de verificación...
                  {validationError && (
                    <span className="d-block mt-2 small">{validationError}</span>
                  )}
                </p>
              </div>
            )}

            {/* Vista: Should Send Code - Botón para enviar código */}
            {verificationStatus === 'should_send' && (
              <>
                {phoneNumber && phoneNumber.trim() !== '' ? (
                  <>
                    <p className="lead text-center mb-4">
                      Te enviaremos un código de verificación al
                    </p>
                    <p className="text-center mb-4">
                      <strong className="fs-5">{phoneNumber}</strong>
                    </p>
                  </>
                ) : (
                  <div className="alert alert-light-warning mb-4" role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <p className="mb-0">
                      No hay número de teléfono registrado. Por favor, ingresa tu número para continuar.
                    </p>
                  </div>
                )}
                
                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleSendCode}
                  disabled={isSending || !phoneNumber || phoneNumber.trim() === ''}
                >
                  {isSending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Enviando...
                    </>
                  ) : (
                    'Enviar código'
                  )}
                </button>
                
                {sendError && (
                  <div className="alert alert-light-danger" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {sendError}
                    {sendAttempts < 3 && (
                      <p className="mb-0 mt-2 small">
                        Intentos de envío: {sendAttempts} de 3
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Vista: Pending Code - Campo de código y validación */}
            {verificationStatus === 'pending_code' && (
              <>
                <p className="lead text-center mb-2">
                  Te enviamos un código SMS al
                </p>
                <p className="text-center mb-3">
                  <strong className="fs-5">{phoneNumber}</strong>
                </p>

                {/* Error de validación general (cuando no hay 6 dígitos) */}
                {validationError && otpCode.length < 6 && (
                  <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {validationError}
                  </div>
                )}

                {/* Campo para ingresar código OTP */}
                <div className="mb-4 mt-5">
                  <label htmlFor="otp-code" className="form-label text-center d-block">
                    Código de verificación
                  </label>
                  <input
                    type="text"
                    id="otp-code"
                    className={`form-control form-control-lg text-center ${validationError && otpCode.length === 6 ? 'is-invalid' : ''}`}
                    value={otpCode}
                    onChange={handleOtpChange}
                    onKeyPress={handleKeyPress}
                    placeholder="000000"
                    maxLength="6"
                    disabled={isValidating}
                    autoFocus
                    aria-label="Código de verificación de 6 dígitos"
                    aria-describedby="otp-helper-text"
                    style={{ letterSpacing: '0.5rem', fontSize: '1.5rem' }}
                  />
                  {!validationError && (
                    <small id="otp-helper-text" className="form-text text-muted d-block text-center mt-2">
                      El código vence en 120 minutos
                    </small>
                  )}
                  {validationError && otpCode.length === 6 && (
                    <div className="invalid-feedback d-block text-center">
                      {validationError}
                    </div>
                  )}
                </div>

                {/* Botón de validación */}
                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleValidateOtp}
                  disabled={isValidating || otpCode.length !== 6}
                >
                  {isValidating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Validando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>

                {/* Acción secundaria - Reenviar código */}
                <div className="text-center">
                  <p className="text-muted small mb-0">
                    ¿No llegó el código?{' '}
                    <button 
                      className="rg-btn rg-btn--link phone-validation-step__link-btn"
                      onClick={handleSendCode}
                      disabled={isSending || sendAttempts >= 3}
                      style={{ verticalAlign: 'baseline' }}
                    >
                      Reenviar SMS
                    </button>
                  </p>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Vista: Cambiar número de teléfono */}
            {sendAttempts >= 3 ? (
              <>
                {/* Máximo de intentos alcanzados - Contactar soporte */}
                <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2 d-block" />
                    <strong>Has alcanzado el máximo de intentos</strong>
                    <p className="mb-0 mt-2">
                      Por favor contacta al soporte para obtener ayuda.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Formulario para editar teléfono */}
                {/* Mostrar mensaje apropiado según el contexto */}
                {sendAttempts === 0 && verifyAttempts === 0 ? (
                  // Activado por 404 - No hay teléfono registrado
                  <div className="alert alert-light-info py-2 px-3 mb-3 d-flex align-items-center" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faPhone} className="me-2 fs-5" />
                    <div>
                      <div className="fw-semibold small mb-1">Ingresa tu número de teléfono</div>
                      <div className="small">
                        {validationError || 'Para continuar necesitamos verificar tu número de teléfono'}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Activado por intentos máximos alcanzados
                  !validationError || !validationError.includes('máximo de intentos') ? (
                    <div className="alert alert-light-warning py-2 px-3 mb-3 d-flex align-items-center" role="alert" aria-live="polite">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 fs-5" />
                      <div>
                        <div className="fw-semibold small mb-1">Máximo de intentos alcanzado</div>
                        <div className="small">Actualiza tu número para recibir un nuevo código</div>
                      </div>
                    </div>
                  ) : null
                )}

                {/* Error de validación en vista de edición - solo si no es el mensaje inicial de 404 */}
                {validationError && sendAttempts > 0 && (
                  <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {validationError}
                  </div>
                )}

                <div className="mb-4">
                  <label htmlFor="new-phone" className="form-label">
                    {sendAttempts === 0 && verifyAttempts === 0 ? 'Número de teléfono' : 'Nuevo número de teléfono'}
                  </label>
                  <PhoneField
                    id="new-phone"
                    name="newPhone"
                    label=""
                    placeholder="Ej: 11 2345 6789"
                    defaultCountry="AR"
                    disabled={isUpdatingPhone}
                    autoFocus
                    valueE164={newPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    onBlur={handlePhoneNumberChange}
                  />
                  <small className="form-text text-muted">
                    Ingresa un número de teléfono válido
                  </small>
                </div>

                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleUpdatePhone}
                  disabled={isUpdatingPhone || !newPhoneNumber}
                >
                  {isUpdatingPhone ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {sendAttempts === 0 && verifyAttempts === 0 ? 'Enviando código...' : 'Actualizando...'}
                    </>
                  ) : (
                    sendAttempts === 0 && verifyAttempts === 0 ? 'Continuar' : 'Actualizar y continuar'
                  )}
                </button>

                {/* Solo mostrar botón cancelar si no fue activado por 404 (hay estado previo) */}
                {(sendAttempts > 0 || verifyAttempts > 0) && (
                  <button
                    className="rg-btn rg-btn--link rg-btn--full phone-validation-step__cancel-btn"
                    onClick={() => {
                      setShowEditPhone(false);
                      setNewPhoneNumber('');
                      setValidationError(null);
                    }}
                    disabled={isUpdatingPhone}
                  >
                    Cancelar
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneValidationStep;

